import React, {useCallback, useMemo} from 'react';
import {Button, MenuItem} from '@blueprintjs/core';
import {Select} from '@blueprintjs/select';
import styled from 'styled-components';

import {StyledFormGroup} from './shared';


const SelectButton = styled(Button)`
    &[class*="bp"][class*="-button"] {
        border-radius: var(--sf-border-radius) !important;
        width: ${p => p.fill ? '100%' : 'fit-content'};
        justify-content: space-between;
        background: transparent !important;
        box-shadow: inset 0 0 0 1px rgba(17, 20, 24, 0.2) !important;
    }
`;

const IS_TOUCH = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const EMPTY_ITEM = {label: '\u2014 Empty \u2014', value: ''};

const filterItem = (query, item) => {
    if (!query) return true;
    return item.label.toLowerCase().includes(query.toLowerCase());
};

const SelectField = ({label, value, options: initialOptions, disabled, error, icon, required, allowCreate, fill, matchTargetWidth, onChange}) => {
    const intent = error ? 'danger' : 'none';

    const options = useMemo(() => {
        const sorted = [...initialOptions].sort((a, b) => a.label.localeCompare(b.label));
        if (allowCreate && value && !sorted.find(o => o.value === value)) {
            return [...sorted, {label: String(value), value}];
        }
        return sorted;
    }, [initialOptions, value, allowCreate]);

    const allItems = required ? options : [EMPTY_ITEM, ...options];
    const selected = options.find(o => o.value === value);
    const displayText = selected?.label ?? (required ? 'Not selected' : EMPTY_ITEM.label);

    const handleCreateItem = useCallback((query) => ({label: query, value: query}), []);
    const renderCreateItem = useCallback((query, active, handleClick) => (
        <MenuItem key="__create__" icon="add" text={`Use "${query}"`} active={active} onClick={handleClick} />
    ), []);

    return (
        <StyledFormGroup label={label} helperText={error?.message} intent={intent}>
            <Select
                items={allItems}
                itemRenderer={(item, {handleClick, modifiers, index}) => (
                    <MenuItem
                        key={`${item.value}-${index}`}
                        text={item.label}
                        active={modifiers.active}
                        selected={item.value === value}
                        disabled={modifiers.disabled}
                        onClick={handleClick}
                    />
                )}
                itemPredicate={filterItem}
                onItemSelect={(item) => onChange(item.value || undefined)}
                disabled={disabled}
                resetOnClose={true}
                popoverProps={{minimal: true, matchTargetWidth: matchTargetWidth ?? !!fill, popoverClassName: 'sf-select-popover'}}
                popoverContentProps={{style: {maxHeight: 450, overflowY: 'auto'}}}
                filterable={true}
                inputProps={IS_TOUCH ? {autoFocus: false} : undefined}
                createNewItemFromQuery={allowCreate ? handleCreateItem : undefined}
                createNewItemRenderer={allowCreate ? renderCreateItem : undefined}
            >
                <SelectButton
                    text={displayText}
                    rightIcon="chevron-down"
                    icon={icon}
                    disabled={disabled}
                    fill={fill}
                />
            </Select>
        </StyledFormGroup>
    );
};

export {SelectField};
